import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  Home,
  ChevronRight,
  MapPin,
  User,
  Briefcase,
  Image as ImageIcon,
  FileText,
  Save,
} from "lucide-react";

import { logoutUser } from "../../redux/slices/authSlice";
import { resetBusinessUserState } from "../../redux/slices/businessuserprofileupdate/businessuserprofileupdateslice";
import { updateBusinessUser } from "../../redux/apis/Businessuser/updateBusinessUserProfile";
import { fetchUserData } from "../../redux/buuserdashboard/userSlice";

const ProfileEditorPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const shouldReduceMotion = useReducedMotion();

  const user = useSelector((state) => state.businessUserProfile.data);
  const token = useSelector((state) => state.auth.token);

  const { error } = useSelector((state) => state.businessUser);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    description: "",
    profession: "",
    dob: "",
    gender: "",
    registration: "",
    village: "",
    city: "",
    district: "",
    state: "",
    country: "",
    postalCode: "",
    googleMapLink: "",
    profilePic: null,
  });

  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // =========================================================
  // FETCH USER DATA
  // =========================================================

  useEffect(() => {
    dispatch(fetchUserData());
  }, [dispatch]);

  // =========================================================
  // PREFILL FORM
  // =========================================================

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        fullName: user.fullName || "",
        email: user.email || "",
        phone: user.phone || "",
        description: user.description || "",
        profession: user.profession || "",
        dob: user.dob ? user.dob.split("T")[0] : "",
        gender: user.gender?.toLowerCase() || "",
        registration: user.registration || "",
        village: user.village || "",
        city: user.city || "",
        district: user.district || "",
        state: user.state || "",
        country: user.country || "",
        postalCode: user.postalCode || "",
        googleMapLink: user.googleMapLink || "",
      }));
    }
  }, [user]);

  // =========================================================
  // HANDLE CHANGE
  // =========================================================

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      setFormData((prev) => ({
        ...prev,
        profilePic: files?.[0] || null,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // =========================================================
  // HANDLE SUBMIT
  // =========================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user?._id) return;

    setIsSubmitting(true);
    setMessage("");

    try {
      const resultAction = await dispatch(
        updateBusinessUser({
          userId: user._id,
          formData,
          currentUser: user,
          token,
        }),
      );

      if (updateBusinessUser.fulfilled.match(resultAction)) {
        const payload = resultAction.payload;

        setMessage(payload?.message || "Profile updated successfully");

        const shouldLogout = payload?.forceLogout || payload?.updatedCritical;

        if (shouldLogout) {
          setTimeout(() => {
            dispatch(logoutUser());
            dispatch(resetBusinessUserState());
            navigate("/login");
          }, 2000);
        }
      } else {
        setMessage(resultAction.payload || "Profile update failed.");
      }
    } catch {
      setMessage("Unexpected error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // =========================================================
  // COMMON INPUT CLASS
  // =========================================================

  const inputClass = `
    w-full
    rounded-xl
    border
    border-slate-300
    bg-white
    px-4
    py-3
    text-sm
    text-slate-800
    outline-none
    transition-all
    duration-200
    placeholder:text-slate-400
    focus:border-sky-500
    focus:ring-4
    focus:ring-sky-500/10
    disabled:cursor-not-allowed
    disabled:bg-slate-100
  `;

  // =========================================================
  // SECTION HEADER
  // =========================================================

  const SectionHeader = ({ icon: Icon, title, description }) => (
    <div className="mb-5 flex items-center gap-3">
      <div
        className="
          flex
          h-10
          w-10
          shrink-0
          items-center
          justify-center
          rounded-xl
          bg-[linear-gradient(135deg,rgb(26,58,92),rgb(14,165,233))]
          text-white
          shadow-md
        "
      >
        <Icon size={18} strokeWidth={2} />
      </div>

      <div className="min-w-0">
        <h3
          className="
            text-base
            font-bold
            text-slate-800
            sm:text-lg
          "
        >
          {title}
        </h3>

        <p className="text-xs text-slate-500 sm:text-sm">{description}</p>
      </div>
    </div>
  );

  // =========================================================
  // FIELD
  // =========================================================

  const Field = ({ label, name, type = "text", value, placeholder = "" }) => (
    <div className="min-w-0">
      <label
        htmlFor={name}
        className="
          mb-1.5
          block
          text-xs
          font-semibold
          text-slate-600
          sm:text-sm
        "
      >
        {label}
      </label>

      <input
        id={name}
        type={type}
        name={name}
        value={value}
        onChange={handleChange}
        disabled={isSubmitting}
        placeholder={placeholder}
        className={inputClass}
      />
    </div>
  );

  return (
    <div
      className="
        relative
        flex
        h-screen
        w-full
        flex-col
        overflow-hidden
        bg-[linear-gradient(145deg,rgb(15,31,51)_0%,rgb(26,58,92)_50%,rgb(30,73,118)_100%)]
      "
    >
      {/* =====================================================
          BACKGROUND DECORATION
          ===================================================== */}

      <motion.div
        initial={
          shouldReduceMotion
            ? false
            : {
                opacity: 0,
                scale: 0.85,
              }
        }
        animate={{
          opacity: 1,
          scale: 1,
        }}
        transition={{
          duration: 1.2,
          delay: 0.25,
          ease: [0.22, 1, 0.36, 1],
        }}
        className="
          pointer-events-none
          absolute
          -left-[230px]
          -top-[230px]
          h-[460px]
          w-[460px]
          rounded-full
          border
          border-sky-300/20
          bg-sky-500/[0.08]
        "
      />

      <motion.div
        initial={
          shouldReduceMotion
            ? false
            : {
                opacity: 0,
                scale: 0.8,
              }
        }
        animate={{
          opacity: 1,
          scale: 1,
        }}
        transition={{
          duration: 1.4,
          delay: 0.4,
          ease: [0.22, 1, 0.36, 1],
        }}
        className="
          pointer-events-none
          absolute
          -left-[145px]
          -top-[145px]
          h-[290px]
          w-[290px]
          rounded-full
          border
          border-sky-200/15
          bg-sky-500/[0.05]
        "
      />

      <motion.div
        initial={
          shouldReduceMotion
            ? false
            : {
                opacity: 0,
                scale: 0.85,
              }
        }
        animate={{
          opacity: 1,
          scale: 1,
        }}
        transition={{
          duration: 1.3,
          delay: 0.35,
          ease: [0.22, 1, 0.36, 1],
        }}
        className="
          pointer-events-none
          absolute
          -bottom-[260px]
          -right-[260px]
          h-[520px]
          w-[520px]
          rounded-full
          border
          border-sky-300/20
          bg-sky-500/[0.07]
        "
      />

      <motion.div
        initial={
          shouldReduceMotion
            ? false
            : {
                opacity: 0,
                scale: 0.8,
              }
        }
        animate={{
          opacity: 1,
          scale: 1,
        }}
        transition={{
          duration: 1.5,
          delay: 0.5,
          ease: [0.22, 1, 0.36, 1],
        }}
        className="
          pointer-events-none
          absolute
          -bottom-[165px]
          -right-[165px]
          h-[330px]
          w-[330px]
          rounded-full
          border
          border-sky-200/15
          bg-sky-500/[0.04]
        "
      />

      {/* =====================================================
          FIXED HEADER
          ===================================================== */}

      <header
        className="
          relative
          z-30
          shrink-0
          border-b
          border-white/10
          bg-[rgb(15,31,51)]/85
          shadow-lg
          backdrop-blur-xl
        "
      >
        <div
          className="
            mx-auto
            flex
            w-full
            max-w-6xl
            items-center
            justify-between
            gap-3
            px-3
            py-2.5
            sm:px-5
            sm:py-3
            md:px-8
            lg:px-10
          "
        >
          {/* LEFT HEADER */}

          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            {/* LOGO / HOME */}

            <button
              type="button"
              onClick={() => navigate("/Home")}
              title="Go to Home"
              className="
                group
                flex
                shrink-0
                items-center
                justify-center
                rounded-xl
                border
                border-white/10
                bg-white/5
                p-1
                transition-all
                duration-200
                hover:bg-white/10
                hover:shadow-lg
              "
            >
              <img
                src="/logo.jpeg"
                alt="MR Health"
                className="
                  h-10
                  w-auto
                  rounded-lg
                  object-contain
                  sm:h-11
                  md:h-12
                "
              />
            </button>

            <ChevronRight size={16} className="shrink-0 text-slate-500" />

            {/* HOME ICON */}

            <button
              type="button"
              onClick={() => navigate("/Home")}
              title="Home"
              className="
                hidden
                items-center
                gap-1.5
                rounded-lg
                px-2
                py-1.5
                text-slate-300
                transition
                hover:bg-white/10
                hover:text-white
                sm:flex
              "
            >
              <Home size={15} />
              <span className="text-xs font-medium">Home</span>
            </button>

            <ChevronRight
              size={15}
              className="hidden shrink-0 text-slate-500 sm:block"
            />

            {/* CURRENT PAGE */}

            <div
              className="
                flex
                min-w-0
                items-center
                gap-2
                rounded-lg
                bg-white/10
                px-2.5
                py-1.5
                backdrop-blur-sm
              "
            >
              <User size={15} className="shrink-0 text-sky-300" />

              <span
                className="
                  truncate
                  text-xs
                  font-semibold
                  text-white
                  sm:text-sm
                "
              >
                Edit Profile
              </span>
            </div>
          </div>

          {/* RIGHT HOME BUTTON */}

          <button
            type="button"
            onClick={() => navigate("/Home")}
            className="
              inline-flex
              shrink-0
              items-center
              gap-2
              rounded-xl
              border
              border-white/15
              bg-white/10
              px-3
              py-2
              text-xs
              font-semibold
              text-white
              shadow-lg
              backdrop-blur-md
              transition-all
              duration-200
              hover:-translate-y-0.5
              hover:bg-white/15
              hover:shadow-xl
              active:translate-y-0
              sm:px-4
              sm:py-2.5
              sm:text-sm
            "
          >
            <Home size={16} strokeWidth={2} />

            <span className="hidden sm:inline">Home</span>
          </button>
        </div>
      </header>

      {/* =====================================================
          SCROLLABLE MAIN AREA
          ===================================================== */}

      <main
        className="
          relative
          z-10
          min-h-0
          flex-1
          overflow-y-auto
          overflow-x-hidden
          scroll-smooth
        "
      >
        <div
          className="
            mx-auto
            w-full
            max-w-6xl
            px-3
            py-4
            sm:px-5
            sm:py-5
            md:px-8
            md:py-6
            lg:px-10
            lg:py-7
          "
        >
          {/* =================================================
              PAGE TITLE
              ================================================= */}

          <motion.div
            initial={
              shouldReduceMotion
                ? false
                : {
                    opacity: 0,
                    y: 20,
                  }
            }
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.7,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="mb-5 sm:mb-6"
          >
            <p
              className="
                mb-1
                text-[10px]
                font-bold
                uppercase
                tracking-[0.22em]
                text-sky-300
                sm:text-xs
              "
            >
              Business Account
            </p>

            <h1
              className="
                text-2xl
                font-bold
                tracking-tight
                text-white
                sm:text-3xl
                lg:text-4xl
              "
            >
              Edit Business Profile
            </h1>

            <p
              className="
                mt-1.5
                max-w-xl
                text-xs
                leading-relaxed
                text-slate-300
                sm:mt-2
                sm:text-sm
                lg:text-base
              "
            >
              Update your professional and business information.
            </p>
          </motion.div>

          {/* =================================================
              PROFILE CARD
              ================================================= */}

          <motion.div
            initial={
              shouldReduceMotion
                ? false
                : {
                    opacity: 0,
                    y: 25,
                  }
            }
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.8,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="
              overflow-hidden
              rounded-2xl
              border
              border-white/20
              bg-white/95
              shadow-[0_25px_70px_rgba(0,0,0,0.28)]
              backdrop-blur-xl
              sm:rounded-3xl
            "
          >
            {/* =================================================
                PROFILE HEADER
                ================================================= */}

            <div
              className="
                bg-[linear-gradient(135deg,rgb(26,58,92),rgb(14,165,233))]
                px-4
                py-5
                sm:px-7
                sm:py-6
                lg:px-8
              "
            >
              <div
                className="
                  flex
                  flex-col
                  gap-4
                  sm:flex-row
                  sm:items-center
                "
              >
                {/* PROFILE IMAGE */}

                <div className="relative shrink-0">
                  <div
                    className="
                      h-20
                      w-20
                      overflow-hidden
                      rounded-full
                      border-4
                      border-white/80
                      bg-white/20
                      shadow-lg
                      sm:h-24
                      sm:w-24
                    "
                  >
                    {user?.profilePic ? (
                      <img
                        src={user.profilePic}
                        alt="Profile"
                        className="
                          h-full
                          w-full
                          object-cover
                        "
                      />
                    ) : (
                      <div
                        className="
                          flex
                          h-full
                          w-full
                          items-center
                          justify-center
                          text-2xl
                          font-bold
                          text-white
                        "
                      >
                        {formData.fullName?.charAt(0)?.toUpperCase() || "U"}
                      </div>
                    )}
                  </div>

                  {/* ONLINE DOT */}

                  <div
                    className="
                      absolute
                      bottom-1
                      right-1
                      h-4
                      w-4
                      rounded-full
                      border-2
                      border-white
                      bg-emerald-400
                      shadow
                    "
                  />
                </div>

                {/* PROFILE DETAILS */}

                <div className="min-w-0">
                  <h2
                    className="
                      truncate
                      text-xl
                      font-bold
                      text-white
                      sm:text-2xl
                    "
                  >
                    {formData.fullName || "Business Profile"}
                  </h2>

                  <p
                    className="
                      mt-1
                      truncate
                      text-sm
                      text-sky-100
                    "
                  >
                    {formData.profession || "Professional Account"}
                  </p>

                  {formData.email && (
                    <p
                      className="
                        mt-1
                        truncate
                        text-xs
                        text-sky-200
                      "
                    >
                      {formData.email}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* =================================================
                FORM
                ================================================= */}

            <form
              onSubmit={handleSubmit}
              className="
                p-4
                sm:p-6
                lg:p-8
              "
            >
              {/* =================================================
                  PERSONAL INFORMATION
                  ================================================= */}

              <section className="mb-7">
                <SectionHeader
                  icon={User}
                  title="Personal Information"
                  description="Basic professional information"
                />

                <div
                  className="
                    grid
                    grid-cols-1
                    gap-4
                    sm:grid-cols-2
                    lg:grid-cols-3
                  "
                >
                  <Field
                    label="Full Name"
                    name="fullName"
                    value={formData.fullName}
                  />

                  <Field
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                  />

                  <Field
                    label="Phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                  />

                  <Field
                    label="Profession"
                    name="profession"
                    value={formData.profession}
                  />

                  <Field
                    label="Date of Birth"
                    name="dob"
                    type="date"
                    value={formData.dob}
                  />

                  {/* GENDER */}

                  <div className="min-w-0">
                    <label
                      htmlFor="gender"
                      className="
                        mb-1.5
                        block
                        text-xs
                        font-semibold
                        text-slate-600
                        sm:text-sm
                      "
                    >
                      Gender
                    </label>

                    <select
                      id="gender"
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className={inputClass}
                    >
                      <option value="">Select Gender</option>

                      <option value="male">Male</option>

                      <option value="female">Female</option>

                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
              </section>

              {/* =================================================
                  PROFESSIONAL DETAILS
                  ================================================= */}

              <section
                className="
                  mb-7
                  border-t
                  border-slate-200
                  pt-7
                "
              >
                <SectionHeader
                  icon={Briefcase}
                  title="Professional Details"
                  description="Registration and professional information"
                />

                <div
                  className="
                    grid
                    grid-cols-1
                    gap-4
                    sm:grid-cols-2
                    lg:grid-cols-3
                  "
                >
                  <Field
                    label="Registration"
                    name="registration"
                    value={formData.registration}
                  />
                </div>
              </section>

              {/* =================================================
                  LOCATION DETAILS
                  ================================================= */}

              <section
                className="
                  mb-7
                  border-t
                  border-slate-200
                  pt-7
                "
              >
                <SectionHeader
                  icon={MapPin}
                  title="Location Details"
                  description="Business address and location"
                />

                <div
                  className="
                    grid
                    grid-cols-1
                    gap-4
                    sm:grid-cols-2
                    lg:grid-cols-3
                  "
                >
                  <Field
                    label="Village"
                    name="village"
                    value={formData.village}
                  />

                  <Field label="City" name="city" value={formData.city} />

                  <Field
                    label="District"
                    name="district"
                    value={formData.district}
                  />

                  <Field label="State" name="state" value={formData.state} />

                  <Field
                    label="Country"
                    name="country"
                    value={formData.country}
                  />

                  <Field
                    label="Postal Code"
                    name="postalCode"
                    type="number"
                    value={formData.postalCode}
                  />
                </div>
              </section>

              {/* =================================================
                  DESCRIPTION
                  ================================================= */}

              <section
                className="
                  mb-7
                  border-t
                  border-slate-200
                  pt-7
                "
              >
                <SectionHeader
                  icon={FileText}
                  title="Business Description"
                  description="Describe your business or professional services"
                />

                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  rows={5}
                  placeholder="Describe your business or professional services..."
                  className={`
                    ${inputClass}
                    resize-none
                  `}
                />
              </section>

              {/* =================================================
                  GOOGLE MAP
                  ================================================= */}

              <section
                className="
                  mb-7
                  border-t
                  border-slate-200
                  pt-7
                "
              >
                <SectionHeader
                  icon={MapPin}
                  title="Google Maps Location"
                  description="Add your business location"
                />

                <input
                  id="googleMapLink"
                  type="url"
                  name="googleMapLink"
                  value={formData.googleMapLink}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  placeholder="https://maps.google.com/..."
                  className={inputClass}
                />
              </section>

              {/* =================================================
                  PROFILE PICTURE
                  ================================================= */}

              <section
                className="
                  mb-7
                  border-t
                  border-slate-200
                  pt-7
                "
              >
                <SectionHeader
                  icon={ImageIcon}
                  title="Profile Picture"
                  description="Upload a new profile image"
                />

                <div
                  className="
                    rounded-2xl
                    border
                    border-dashed
                    border-sky-300
                    bg-sky-50/70
                    p-4
                    transition-all
                    duration-200
                    hover:border-sky-500
                    hover:bg-sky-50
                    sm:p-5
                  "
                >
                  <div
                    className="
                      flex
                      flex-col
                      gap-4
                      sm:flex-row
                      sm:items-center
                    "
                  >
                    {user?.profilePic && (
                      <img
                        src={user.profilePic}
                        alt="Current profile"
                        className="
                          h-16
                          w-16
                          shrink-0
                          rounded-xl
                          object-cover
                          shadow
                        "
                      />
                    )}

                    <div className="min-w-0 flex-1">
                      <input
                        type="file"
                        name="profilePic"
                        accept="image/*"
                        onChange={handleChange}
                        disabled={isSubmitting}
                        className="
                          block
                          w-full
                          cursor-pointer
                          text-xs
                          text-slate-600
                          file:mr-3
                          file:rounded-lg
                          file:border-0
                          file:bg-[rgb(26,58,92)]
                          file:px-4
                          file:py-2
                          file:text-xs
                          file:font-semibold
                          file:text-white
                          hover:file:bg-[rgb(14,165,233)]
                          sm:text-sm
                        "
                      />

                      <p
                        className="
                          mt-2
                          text-xs
                          text-slate-500
                        "
                      >
                        JPG, PNG or other supported image formats.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* =================================================
                  ACTION AREA
                  ================================================= */}

              <section
                className="
                  flex
                  flex-col
                  gap-4
                  border-t
                  border-slate-200
                  pt-7
                  sm:flex-row
                  sm:items-center
                  sm:justify-between
                "
              >
                {/* MESSAGE */}

                <div className="min-h-[24px]">
                  {message && (
                    <div
                      className={`
                        rounded-lg
                        px-3
                        py-2
                        text-xs
                        font-medium
                        sm:text-sm
                        ${
                          message.toLowerCase().includes("success")
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-red-50 text-red-700"
                        }
                      `}
                    >
                      {message}
                    </div>
                  )}

                  {error && (
                    <div
                      className="
                        mt-2
                        rounded-lg
                        bg-red-50
                        px-3
                        py-2
                        text-xs
                        font-medium
                        text-red-700
                        sm:text-sm
                      "
                    >
                      {error}
                    </div>
                  )}
                </div>

                {/* UPDATE BUTTON */}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`
                    inline-flex
                    w-full
                    items-center
                    justify-center
                    gap-2
                    rounded-xl
                    px-6
                    py-3
                    text-sm
                    font-semibold
                    text-white
                    shadow-lg
                    transition-all
                    duration-200
                    sm:w-auto
                    sm:min-w-[190px]
                    ${
                      isSubmitting
                        ? "cursor-not-allowed bg-slate-400"
                        : "bg-[linear-gradient(135deg,rgb(26,58,92),rgb(14,165,233))] hover:-translate-y-0.5 hover:shadow-xl active:translate-y-0"
                    }
                  `}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="
                          h-4
                          w-4
                          animate-spin
                          rounded-full
                          border-2
                          border-white/40
                          border-t-white
                        "
                      />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save size={17} />
                      Update Profile
                    </>
                  )}
                </button>
              </section>
            </form>
          </motion.div>

          {/* =================================================
              FOOTER
              ================================================= */}

          <p
            className="
              py-5
              text-center
              text-xs
              text-slate-400
            "
          >
            Keep your business profile information accurate and up to date.
          </p>
        </div>
      </main>
    </div>
  );
};

export default ProfileEditorPage;
